// @flow

import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import moment from "moment";
import config from "@config";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  SectionComponent,
  GSGrid,
  SchemaForm,
} from "@components";
import actionCreators from "@modules/reporting/actionCreator";
import {
  showLogReferralDetailsModal,
  selectedLogReferralDetails,
  selectReferralCommonData,
} from "@modules/reporting/selectors/referral-tracker";
import type {
  CommonData,
  ReferralLogDetails,
} from "@modules/reporting/reducer/referral-tracker/types";
import { View } from "@components";

type OwnProps = $Exact<{
  data: number,
}>;

type ComponentState = $Exact<{
  formData: ?Object,
}>;
type PropsFromState = $Exact<{
  showModal: boolean,
  details: Array<ReferralLogDetails>,
  commonData: CommonData,
}>;

type PropsFromDispatch = $Exact<{
  toggleLogReferralDetailsModal: typeof actionCreators.ToggleLogReferralDetailsModal,
  submitLogReferralData: typeof actionCreators.SubmitLogReferralData,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class ReferralLogModal extends Component<Props, ComponentState> {
  constructor(props: Props) {
    super(props);
    // Class components needs constructor for accessing the SchemaForm's ref
    this.formRef = React.createRef();
  }
  state = {
    formData: null,
    key: Date.now(),
  };

  handleCancel = () => {
    this.props.toggleLogReferralDetailsModal();
  };
  submitForm = () => {
    if (this.formRef) {
      this.formRef.current.submit();
    }
  };
  handleChange = (formData: Object) => {
    this.setState({
      formData,
    });
  };
  handleSubmit = (formData: Object) => {
    this.props.submitLogReferralData({
      clientId: this.props.commonData.clientId,
      supplierReferralId: this.props.commonData.referralId,
      requestBody: {
        outcome: { id: formData.outcome },
        comment: formData.comment,
      },
    });
    this.setState({
      formData: null,
      key: Date.now(),
    });
  };
  get uiSchema() {
    return {
      comment: {
        "ui:widget": "textarea",
        "ui:options": {
          rows: 4,
        },
      },
    };
  }

  get schema() {
    return {
      required: ["outcome", "comment"],
      type: "object",
      properties: {
        outcome: {
          title: this.props.t("TREATMENT_OUTCOME"),
          type: "number",
          oneOf: this.props.commonData.outcomeOptions.map(o => {
            return { const: o.id, title: o.outcome };
          }),
        },
        comment: {
          title: "Comments",
          type: "string",
        },
      },
    };
  }

  render() {
    if (!this.props.showModal) {
      return null;
    }
    return (
      <Modal className="modal-normal">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t("TREATMENT_OUTCOMES")} #{this.props.commonData.referralId}
        </ModalHeader>
        <ModalBody>
          <View padding={4} gap={10}>
            <View>
              <SchemaForm
                ref={this.formRef}
                formData={this.state.formData}
                schema={this.schema}
                onSubmit={this.handleSubmit}
                onChange={this.handleChange}
                uiSchema={this.uiSchema}
                key={this.state.key}
                externalSubmit
              />
              <View alignItems="end">
                <Button
                  onClick={this.submitForm}
                  className="mt-4"
                  materialIcon="replay_circle_filled"
                >
                  {this.props.t("SUBMIT")}
                </Button>
              </View>
            </View>

            <View gap={5}>
              <h5>{this.props.t("TREATMENT_OUTCOME_HISTORY")}</h5>
              <GSGrid
                rows={this.props.details}
                columns={[
                  {
                    name: this.props.t("DATE_TIME"),
                    key: "createdAt",
                    renderCell: ({ row }: { row: Object }) =>
                      moment(row.createdAt).format(config.dateTimeFormat),
                  },
                  {
                    name: this.props.t("USER"),
                    key: "createdBy",
                    renderCell: ({ row }: { row: Object }) =>
                      `${row.createdBy.firstName} ${row.createdBy.lastName}`,
                  },
                  {
                    name: this.props.t("SELECTED_ACTION"),
                    key: "action",
                    align: "left",
                    width: 310,
                    renderCell: ({ row }: *) => GSGrid.render.ellipsis(`${row.outcome.outcome}`),
                  },
                  {
                    name: this.props.t("ADDITIONAL_COMMENTS"),
                    key: "comment",
                    renderCell: ({ row }: { row: Object }) => `${row.comment}`,
                  },
                ]}
              />
            </View>
          </View>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CLOSE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state): PropsFromState => {
  return {
    showModal: showLogReferralDetailsModal(state),
    details: selectedLogReferralDetails(state),
    commonData: selectReferralCommonData(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  toggleLogReferralDetailsModal: actionCreators.ToggleLogReferralDetailsModal,
  submitLogReferralData: actionCreators.SubmitLogReferralData,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(ReferralLogModal);
